import axios, { AxiosProgressEvent } from 'axios';
import { IGalleryPhoto } from './nestjs-base-api/types';

interface UploadPhotoParams {
  jwtToken: string;
  galleryId: string;
  files: File[];
  onProgress?: (progress: number) => void;
  onFileProgress?: (fileName: string, progress: number) => void;
}

interface FileUploadProgress {
  fileName: string;
  progress: number;
  uploaded: boolean;
  error?: string;
}

export interface UploadResult {
  successful: IGalleryPhoto[];
  failed: {
    fileName: string;
    error: string;
  }[];
}

type UploadPromiseResult = 
  | { success: true; data: IGalleryPhoto }
  | { success: false; fileName: string; error: string };

export class UploadService {
  private static getApiUrl() {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
    // Ensure the base URL ends with a slash
    return baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
  }

  static async uploadPhotosWithProgress({
    jwtToken,
    galleryId,
    files,
    onProgress,
    onFileProgress
  }: UploadPhotoParams): Promise<UploadResult> {
    const uploadedPhotos: IGalleryPhoto[] = [];
    const failedUploads: { fileName: string; error: string }[] = [];
    const fileProgressMap = new Map<string, number>();
    
    // Initialize progress for all files
    files.forEach(file => {
      fileProgressMap.set(file.name, 0);
    });

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('galleryId', galleryId);
      formData.append('file', file);

      try {
        const response = await axios.post<IGalleryPhoto>(
          `${this.getApiUrl()}galleries/upload`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${jwtToken}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              if (progressEvent.total) {
                const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                fileProgressMap.set(file.name, fileProgress);
                
                // Report individual file progress
                if (onFileProgress) {
                  onFileProgress(file.name, fileProgress);
                }
                
                // Calculate and report overall progress
                if (onProgress) {
                  const completedFiles = i;
                  const currentFileProgress = fileProgress / 100;
                  const overallProgress = ((completedFiles + currentFileProgress) / files.length) * 100;
                  onProgress(Math.round(overallProgress));
                }
              }
            }
          }
        );

        // Add the uploaded photo to our results
        uploadedPhotos.push(response.data);
        fileProgressMap.set(file.name, 100);
        
        // Report completion of this file
        if (onFileProgress) {
          onFileProgress(file.name, 100);
        }
      } catch (error: any) {
        console.error(`Error uploading file ${file.name}:`, error);
        
        // Record the failure
        let errorMessage = 'Upload failed';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        failedUploads.push({
          fileName: file.name,
          error: errorMessage
        });
        
        // Still mark as "complete" for progress tracking
        fileProgressMap.set(file.name, 100);
        
        // Report completion (even though it failed)
        if (onFileProgress) {
          onFileProgress(file.name, 100);
        }
        
        // Update overall progress
        if (onProgress) {
          const completedFiles = i + 1;
          const overallProgress = (completedFiles / files.length) * 100;
          onProgress(Math.round(overallProgress));
        }
      }
    }

    // Ensure final progress is 100%
    if (onProgress) {
      onProgress(100);
    }

    return {
      successful: uploadedPhotos,
      failed: failedUploads
    };
  }

  static async uploadPhotosWithProgressBatch({
    jwtToken,
    galleryId,
    files,
    onProgress,
    onFileProgress
  }: UploadPhotoParams): Promise<UploadResult> {
    const uploadedPhotos: IGalleryPhoto[] = [];
    const failedUploads: { fileName: string; error: string }[] = [];
    const fileProgressMap = new Map<string, FileUploadProgress>();
    
    // Initialize progress tracking for all files
    files.forEach(file => {
      fileProgressMap.set(file.name, {
        fileName: file.name,
        progress: 0,
        uploaded: false
      });
    });

    // Function to calculate overall progress
    const calculateOverallProgress = (): number => {
      let totalProgress = 0;
      fileProgressMap.forEach(fileProgress => {
        totalProgress += fileProgress.progress;
      });
      return Math.round(totalProgress / files.length);
    };

    // Create upload promises for parallel uploads
    const uploadPromises = files.map(async (file): Promise<UploadPromiseResult> => {
      const formData = new FormData();
      formData.append('galleryId', galleryId);
      formData.append('file', file);

      try {
        const response = await axios.post<IGalleryPhoto>(
          `${this.getApiUrl()}galleries/upload`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${jwtToken}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              if (progressEvent.total) {
                const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                
                // Update file progress
                const fileInfo = fileProgressMap.get(file.name);
                if (fileInfo) {
                  fileInfo.progress = fileProgress;
                  fileProgressMap.set(file.name, fileInfo);
                }
                
                // Report individual file progress
                if (onFileProgress) {
                  onFileProgress(file.name, fileProgress);
                }
                
                // Report overall progress
                if (onProgress) {
                  onProgress(calculateOverallProgress());
                }
              }
            }
          }
        );

        // Mark file as uploaded
        const fileInfo = fileProgressMap.get(file.name);
        if (fileInfo) {
          fileInfo.uploaded = true;
          fileInfo.progress = 100;
          fileProgressMap.set(file.name, fileInfo);
        }

        // Return the uploaded photo
        return { success: true as const, data: response.data };
      } catch (error: any) {
        // Mark file with error
        const fileInfo = fileProgressMap.get(file.name);
        if (fileInfo) {
          fileInfo.error = error instanceof Error ? error.message : 'Upload failed';
          fileInfo.progress = 100; // Mark as complete even though it failed
          fileProgressMap.set(file.name, fileInfo);
        }
        
        // Extract error message
        let errorMessage = 'Upload failed';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        return { success: false as const, fileName: file.name, error: errorMessage };
      }
    });

    // Execute all uploads in parallel
    const results = await Promise.allSettled(uploadPromises);
    
    // Process results
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const uploadResult = result.value;
        if (uploadResult.success) {
          uploadedPhotos.push(uploadResult.data);
        } else {
          failedUploads.push({
            fileName: uploadResult.fileName,
            error: uploadResult.error
          });
        }
      } else {
        // This shouldn't happen as we're catching errors above, but just in case
        console.error('Unexpected promise rejection:', result.reason);
      }
    }
    
    // Ensure final progress is 100%
    if (onProgress) {
      onProgress(100);
    }

    return {
      successful: uploadedPhotos,
      failed: failedUploads
    };
  }
}